import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import { guardRegistry, registerDefaultGuards } from './guardRegistry'

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    redirect: '/dashboard',
  },
  // ========== Auth Routes (Chunk: auth) ==========
  {
    path: '/login',
    name: 'login',
    component: () => import(/* webpackChunkName: "auth" */ '@/views/auth/LoginView.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/register',
    name: 'register',
    component: () => import(/* webpackChunkName: "auth" */ '@/views/auth/RegisterView.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/verify-email',
    name: 'verify-email',
    component: () => import(/* webpackChunkName: "auth" */ '@/views/auth/VerifyEmailView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: () => import(/* webpackChunkName: "auth" */ '@/views/auth/ForgotPasswordView.vue'),
    meta: { requiresAuth: false },
  },

  // ========== Dashboard (Chunk: dashboard) ==========
  {
    path: '/dashboard',
    name: 'dashboard',
    component: () => import(/* webpackChunkName: "dashboard" */ '@/views/dashboard/DashboardView.vue'),
    meta: { requiresAuth: true },
  },

  // ========== Mi cuenta / Mis exámenes (Chunk: billing) ==========
  // HU-BILLING-001..004 — saldo, historial del libro propio, precio por
  // cantidad y cómo comprar. Sin pasarela en el lanzamiento (§3.7).
  {
    path: '/mis-examenes',
    name: 'my-exams',
    component: () => import(/* webpackChunkName: "billing" */ '@/views/billing/MyExamsView.vue'),
    meta: { requiresAuth: true, title: 'Mis exámenes' },
  },

  // ========== Mi perfil (Chunk: profile) ==========
  // HU-PROFILE-001 — el profesor edita su propia información. Los campos
  // existen desde el bootstrap y ninguna historia los consumía («Brecha B» de
  // MODELO-DATOS §PROFESSOR_PROFILES); esta pantalla los estrena.
  {
    path: '/mi-perfil',
    name: 'my-profile',
    component: () => import(/* webpackChunkName: "profile" */ '@/views/profile/MyProfileView.vue'),
    meta: { requiresAuth: true, title: 'Mi perfil' },
  },

  // ========== Courses Routes (Chunk: courses) ==========
  {
    path: '/courses',
    name: 'courses',
    component: () => import(/* webpackChunkName: "courses" */ '@/views/courses/CoursesListView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/courses/nuevo',
    name: 'course-create',
    component: () => import(/* webpackChunkName: "courses" */ '@/views/courses/CourseCreateView.vue'),
    meta: { requiresAuth: true },
  },
  // Rutas /courses/:id (detalle) y /courses/:id/edit (edición-página) eliminadas
  // en el rediseño 2026-07-01 (ADR-011): la edición vive en un modal sobre la lista
  // y la pantalla de detalle se retiró (HU-COURSES-005 retirada).

  // ========== Exams Routes (Chunk: exams) ==========
  {
    path: '/exams',
    name: 'exams',
    component: () => import(/* webpackChunkName: "exams" */ '@/views/exams/ExamsListView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/exams/:id/edit',
    name: 'exam-editor',
    component: () => import(/* webpackChunkName: "exams" */ '@/views/exams/ExamEditorView.vue'),
    meta: { requiresAuth: true },
  },

  // ========== Topics Routes (Chunk: topics) ==========
  {
    path: '/topics',
    name: 'topics',
    component: () => import(/* webpackChunkName: "topics" */ '@/views/topics/TopicsListView.vue'),
    meta: { requiresAuth: true },
  },

  // ========== Questions Routes (Chunk: questions) ==========
  {
    path: '/questions',
    name: 'questions',
    component: () => import(/* webpackChunkName: "questions" */ '@/views/questions/QuestionsListView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/questions/create',
    name: 'questions-create',
    component: () => import(/* webpackChunkName: "questions" */ '@/views/questions/QuestionCreateView.vue'),
    meta: { requiresAuth: true },
  },
  // Caso (ADR-012) — rutas antes de /questions/:id/edit para evitar colisión.
  {
    path: '/questions/cases/create',
    name: 'question-case-create',
    component: () => import(/* webpackChunkName: "questions" */ '@/views/questions/CaseEditorView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/questions/cases/:id/edit',
    name: 'question-case-edit',
    component: () => import(/* webpackChunkName: "questions" */ '@/views/questions/CaseEditorView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/questions/:id/edit',
    name: 'questions-edit',
    component: () => import(/* webpackChunkName: "questions" */ '@/views/questions/QuestionDetailView.vue'),
    meta: { requiresAuth: true },
  },

  // (2026-07-14, PERF-NET-02: retiradas las rutas huérfanas
  // /classroom/:classroomId/exam/:examId[/review] — StudentExamView y
  // ProfessorReviewView eran vestigios mock sin navegación; rendir examen
  // vive en Flutter y la revisión en ClassroomResultsView.)

  // ========== Virtual Classrooms (Chunk: classrooms) ==========
  {
    path: '/classrooms',
    name: 'classrooms',
    component: () => import(/* webpackChunkName: "classrooms" */ '@/views/classrooms/ClassroomsListView.vue'),
    meta: { requiresAuth: true, title: 'Aulas Virtuales' },
  },
  {
    path: '/classrooms/create',
    name: 'classroom-create',
    component: () => import(/* webpackChunkName: "classrooms" */ '@/views/classrooms/ClassroomCreateView.vue'),
    meta: { requiresAuth: true, title: 'Crear Aula Virtual' },
  },
  {
    path: '/classrooms/:id/waiting',
    name: 'classroom-waiting',
    component: () => import(/* webpackChunkName: "classrooms" */ '@/views/classrooms/ClassroomWaitingView.vue'),
    meta: { requiresAuth: true, title: 'Sala de Espera' },
  },
  {
    path: '/classrooms/:id/monitor',
    name: 'classroom-monitor',
    component: () => import(/* webpackChunkName: "classrooms" */ '@/views/classrooms/ClassroomMonitorView.vue'),
    meta: { requiresAuth: true, title: 'Supervisar Examen' },
  },
  {
    path: '/classrooms/:id/results',
    name: 'classroom-results',
    component: () => import(/* webpackChunkName: "classrooms" */ '@/views/classrooms/ClassroomResultsView.vue'),
    meta: { requiresAuth: true, title: 'Resultados' },
  },
  // ========== Classroom default redirect ==========
  {
    path: '/classrooms/:id',
    redirect: (to) => `/classrooms/${to.params.id}/waiting`,
  },

  // ========== Not Found ==========
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import(/* webpackChunkName: "errors" */ '@/views/NotFoundView.vue'),
    meta: { requiresAuth: true },
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
})

// Registrar guards por defecto
registerDefaultGuards()

// Navigation guard - Usa el guardRegistry (OCP)
router.beforeEach(async (to, from, next) => {
  await guardRegistry.executeGuards(to, from, next)
})

export default router
